/**
 * 需要グループ CSV の列ヘッダー (6 列、列順固定、
 * docs/specs/admin/data/uploads/demand-groups.md §1 / AC-DUG-001)。
 *
 * アップロード側の期待ヘッダー (`DemandGroupCsvUpload`) とダウンロード側の CSV
 * 組み立て (`AdminUploads`) の 2 箇所が同じ列定義を要求するため、
 * `src/models/` に置く単一 SoT とする (列がどちらか片方だけでずれると
 * アップロード → ダウンロード → 再アップロードの往復が壊れる)。
 * backend usecase/demandgroup.ParseCsv の expectedHeader と同一。
 *
 * NOTE(#6293): UI 文言の「小売事業者」は 2026-08-25 に「小売電気事業者」へ統一したが、
 * **この列名は改名していない**。CSV ヘッダーは backend の expectedHeader および
 * 利用者が既に持っている入稿ファイルとの一致で成立する wire contract で、
 * frontend 単独で変えると取込が全滅する。改名する場合は spec・backend・雛形 CSV・
 * 移行期の旧ヘッダー受理をセットで決める必要がある (未決)。
 */
import { escapeCsvCell } from '~/libs/csv'

export const DEMAND_GROUP_CSV_HEADER = [
  '需要BGコード',
  '小売事業者コード',
  'グループ名',
  '供給地点特定番号',
  '名称',
  '削除フラグ',
]

/**
 * 需要グループ CSV の英語ヘッダー (spec demand-groups.md §1 / AC-DUG-001)。日本語ヘッダーと
 * 同じ列順で、英語表示の雛形・入稿済みデータはこのヘッダーで出す。取込 (backend
 * `demandgroup.ParseCsv` / 画面の DropZone) は日英どちらも受理する (英語は大文字・小文字を
 * 区別しない)。本 CSV に選択肢として決まった値を持つ列は無いので、英語表示で変わるのは
 * ヘッダー行とファイル名だけ (spec §1「英語表示」)。
 */
export const DEMAND_GROUP_CSV_HEADER_EN = [
  'DemandBgCode',
  'RetailerCode',
  'GroupName',
  'SupplyPointSpecificNumber',
  'Name',
  'Delete',
]

/** データ種別名 (ダウンロードファイル名の `{データ種別名}`、`common.md` §6 の対応表)。 */
export function demandGroupCsvDataTypeName(isEnglish: boolean): string {
  return isEnglish ? 'demand-groups' : '需要グループ'
}

/** 需要グループ CSV 1 行分 (6 列、`DEMAND_GROUP_CSV_HEADER` と列順が対応)。 */
export type DemandGroupCsvRow = {
  demandBgCode: string
  retailerCode: string
  groupName: string
  supplyPointSpecificNumber: string
  /** 供給地点のメモ (任意)。未設定 = 空セル。 */
  name: string
  deleteFlag: string
}

/**
 * 需要グループ CSV 本文を組み立てる (ヘッダー行 + 0 件以上のデータ行)。
 * `~/pages/AdminUploads/models/demandGroupCsvExport` (入稿済みデータ DL) と
 * `~/pages/DemandGroupCsvUpload/models/demandGroupCsvTemplate` (雛形 DL) の
 * 2 箇所が同じ列組み立てを必要とするため (bilateral 側の
 * `serializeBilateralTradeCsvRows` と同じ判断)、ここに 1 本だけ置く。
 *
 * 区切りはカンマ、改行は `\r\n` (RFC 4180)。BOM は付けない (`~/libs/downloadCsv`
 * が保存時に付与する)。rows が空でもヘッダー行のみを返す。
 */
export function serializeDemandGroupCsvRows(
  rows: readonly DemandGroupCsvRow[],
  isEnglish: boolean,
): string {
  const headerLine = (
    isEnglish ? DEMAND_GROUP_CSV_HEADER_EN : DEMAND_GROUP_CSV_HEADER
  )
    .map(escapeCsvCell)
    .join(',')
  const bodyLines = rows.map((row) =>
    [
      escapeCsvCell(row.demandBgCode),
      escapeCsvCell(row.retailerCode),
      escapeCsvCell(row.groupName),
      escapeCsvCell(row.supplyPointSpecificNumber),
      escapeCsvCell(row.name),
      escapeCsvCell(row.deleteFlag),
    ].join(','),
  )
  return [headerLine, ...bodyLines].join('\r\n')
}
